'use client'

/**
 * The chips for files on a chat question (#16101).
 *
 *  - {@link ChatAttachmentChips} — the composer's row, shared by Home's ask
 *    bar (`AiAskComposer`) and the docked panel (`AiChatInput`): a thumbnail
 *    or an icon, the name, a pending spinner while it uploads, and a remove
 *    button. Every remove button is a real 44×44 `<button>` with an `sr-only`
 *    label naming the file, so Tab reaches each one.
 *  - {@link SentAttachmentChips} — the read-only row on the user turn that
 *    carried the files, in the shared transcript (`AiChatMessages`), so a Home
 *    conversation reopened in the panel still shows its files. Each opens the
 *    file through the same-origin content route, which holds the attachment's
 *    own view rule (`canViewAttachableParent`).
 *  - {@link ChatAttachButton} — the composer's "Attach files" button and the
 *    hidden multi-file picker it opens, so the two composers cannot drift.
 */

import { useRef } from 'react'

import {
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  X,
} from '@/components/icons'
import type { ChatAttachmentKind } from '@/lib/ai-chat/chat-attachments'
import { chatAttachmentKind } from '@/lib/ai-chat/chat-attachments'
import { HEIC_ACCEPT } from '@/lib/attachments/heic'
import { getSupportedMimeTypes } from '@/lib/storage/file-types'
import { cn } from '@/lib/utils'

import type {
  ChatAttachmentChip,
  ChatAttachments,
  SentChatAttachment,
} from './use-chat-attachments'

/**
 * What the picker offers: every supported type, and HEIC/HEIF so a dialog
 * lists iPhone photos as selectable — they are converted to JPEG before
 * upload (#16099).
 */
const ATTACH_ACCEPT = [...getSupportedMimeTypes(), ...HEIC_ACCEPT].join(',')

/**
 * The "Attach files" button (44×44, `sr-only` name) and the hidden
 * `<input type="file" multiple>` it opens. Disabled at the per-turn cap,
 * when `atCapTitle` says why.
 */
export function ChatAttachButton({
  attachments,
  testId,
  atCapTitle,
}: {
  attachments: ChatAttachments
  testId?: string
  atCapTitle: string
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => fileInputRef.current?.click()}
        disabled={attachments.atCap}
        title={attachments.atCap ? atCapTitle : 'Attach files'}
        className="flex size-8 pointer-coarse:size-11 shrink-0 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-surface-panel hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected disabled:opacity-50"
      >
        <Paperclip className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only">Attach files</span>
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ATTACH_ACCEPT}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        onChange={event => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ''
          if (files.length > 0) attachments.addFiles(files, 'button')
        }}
      />
    </>
  )
}

function KindIcon({ kind }: { kind: ChatAttachmentKind }) {
  const Icon =
    kind === 'image' ? ImageIcon : kind === 'document' ? FileText : Paperclip
  return (
    <Icon className="h-4 w-4 shrink-0 text-text-secondary" aria-hidden="true" />
  )
}

export function ChatAttachmentChips({
  chips,
  onRemove,
  className,
}: {
  chips: readonly ChatAttachmentChip[]
  onRemove: (key: string) => void
  className?: string
}) {
  if (chips.length === 0) return null
  return (
    <ul
      aria-label="Attached files"
      data-testid="chat-attachment-chips"
      className={cn('flex flex-wrap gap-2', className)}
    >
      {chips.map(chip => (
        <li
          key={chip.key}
          data-testid="chat-attachment-chip"
          data-status={chip.status}
          className="flex max-w-60 items-center gap-2 rounded-lg border border-stroke-light bg-surface-elevated py-0 pl-2 text-sm text-text-primary"
        >
          {chip.previewUrl ? (
            // A local object URL of the file being attached: not optimisable.
            <img
              src={chip.previewUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded object-cover"
            />
          ) : (
            <KindIcon kind={chip.kind} />
          )}
          <span className="min-w-0 truncate" title={chip.name}>
            {chip.name}
          </span>
          {chip.status === 'uploading' ? (
            <span className="inline-flex shrink-0 items-center">
              <Loader2
                className="h-4 w-4 animate-spin text-text-secondary"
                aria-hidden="true"
              />
              <span className="sr-only">Uploading</span>
            </span>
          ) : null}
          <button
            type="button"
            data-testid="chat-attachment-remove"
            onClick={() => onRemove(chip.key)}
            className="flex size-8 pointer-coarse:size-11 shrink-0 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-surface-panel hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Remove {chip.name}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}

export function SentAttachmentChips({
  attachments,
}: {
  attachments: readonly SentChatAttachment[]
}) {
  if (attachments.length === 0) return null
  return (
    <ul aria-label="Attached files" className="mb-2 flex flex-wrap gap-2">
      {attachments.map(file => (
        <li key={file.id}>
          <a
            data-testid="chat-turn-attachment"
            href={`/api/attachments/${encodeURIComponent(file.id)}/content`}
            target="_blank"
            rel="noreferrer"
            className="flex pointer-coarse:min-h-11 max-w-60 items-center gap-2 rounded-lg border border-stroke-light bg-surface-elevated px-2 py-1.5 text-sm text-text-primary hover:bg-surface-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected"
          >
            <KindIcon kind={chatAttachmentKind(file.mimeType)} />
            <span className="min-w-0 truncate">{file.fileName}</span>
          </a>
        </li>
      ))}
    </ul>
  )
}
